import axios from 'axios';

const API_BASE = '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ── CSRF Token Management ─────────────────────────────────
let csrfToken: string | null = null;

export async function initCsrf(): Promise<void> {
  try {
    const response = await api.get('/csrf-token');
    csrfToken = response.data.csrfToken;
  } catch (err) {
    console.error('Failed to fetch CSRF token:', err);
  }
}

// Request interceptor: attach active client + CSRF token
// (JWT is now in HttpOnly cookie — sent automatically by browser)
api.interceptors.request.use((config) => {
  // Attach active family office ID for tenant-scoped routes
  const officeId = localStorage.getItem('osprey_active_office');
  if (officeId) {
    config.headers['X-Family-Office-Id'] = officeId;
  }
  // Attach global filters (per-entity / per-member)
  const entityFilter = localStorage.getItem('osprey_filter_entity');
  if (entityFilter) {
    config.headers['X-Filter-Entity-Id'] = entityFilter;
  }
  const memberFilter = localStorage.getItem('osprey_filter_member');
  if (memberFilter) {
    config.headers['X-Filter-Member-Id'] = memberFilter;
  }
  // Attach CSRF token to state-changing requests
  if (config.method && !['get', 'head', 'options'].includes(config.method)) {
    if (csrfToken) {
      config.headers['x-csrf-token'] = csrfToken;
    }
  }
  return config;
});

// Response interceptor: handle 401s
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired — clear local state and redirect to login
      // But don't redirect if already on login/register (prevents infinite loop)
      const path = window.location.pathname;
      if (path !== '/login' && path !== '/register') {
        localStorage.removeItem('osprey_user');
        localStorage.removeItem('osprey_active_office');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// ── Auth API ──────────────────────────────────────────────
export const authApi = {
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    firmName: string;
    advisorType: string;
  }) => api.post('/auth/register', data),

  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),

  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// ── Clients API (advisor's family offices) ────────────────
export const clientsApi = {
  list: () => api.get('/clients'),
  get: (id: string) => api.get(`/clients/${id}`),
  create: (data: { name: string; domicileState: string }) =>
    api.post('/clients', data),
  update: (id: string, data: { name?: string; domicileState?: string }) =>
    api.put(`/clients/${id}`, data),
  delete: (id: string) => api.delete(`/clients/${id}`),
};

// ── Dashboard API ─────────────────────────────────────────
export const dashboardApi = {
  summary: () => api.get('/dashboard/summary'),
  jurisdictions: () => api.get('/dashboard/jurisdictions'),
};

// ── Members API ───────────────────────────────────────────
export const membersApi = {
  list: (params?: { page?: number; limit?: number }) =>
    api.get('/members', { params }),
  get: (id: string) => api.get(`/members/${id}`),
  create: (data: any) => api.post('/members', data),
  update: (id: string, data: any) => api.put(`/members/${id}`, data),
  delete: (id: string) => api.delete(`/members/${id}`),
};

// ── Assets API ────────────────────────────────────────────
export const assetsApi = {
  list: (params?: { page?: number; limit?: number; type?: string }) =>
    api.get('/assets', { params }),
  get: (id: string) => api.get(`/assets/${id}`),
  create: (data: any) => api.post('/assets', data),
  update: (id: string, data: any) => api.put(`/assets/${id}`, data),
  delete: (id: string) => api.delete(`/assets/${id}`),
  summaryByType: () => api.get('/assets/summary/by-type'),
};

// ── Liabilities API ───────────────────────────────────────
export const liabilitiesApi = {
  list: (params?: { page?: number; limit?: number }) =>
    api.get('/liabilities', { params }),
  create: (data: any) => api.post('/liabilities', data),
  update: (id: string, data: any) => api.put(`/liabilities/${id}`, data),
  delete: (id: string) => api.delete(`/liabilities/${id}`),
};

// ── Entities API ──────────────────────────────────────────
export const entitiesApi = {
  list: (params?: { page?: number; limit?: number; type?: string }) =>
    api.get('/entities', { params }),
  get: (id: string) => api.get(`/entities/${id}`),
  create: (data: any) => api.post('/entities', data),
  update: (id: string, data: any) => api.put(`/entities/${id}`, data),
  delete: (id: string) => api.delete(`/entities/${id}`),
  ownershipMap: () => api.get('/entities/ownership-map'),
  listOwnership: (entityId: string) => api.get(`/entities/${entityId}/ownership`),
  addOwnership: (entityId: string, data: any) => api.post(`/entities/${entityId}/ownership`, data),
  updateOwnership: (entityId: string, stakeId: string, data: any) => api.put(`/entities/${entityId}/ownership/${stakeId}`, data),
  deleteOwnership: (entityId: string, stakeId: string) => api.delete(`/entities/${entityId}/ownership/${stakeId}`),
  getSummary: (entityId: string) => api.get(`/entities/${entityId}/summary`),
};

// ── Exit Events API ───────────────────────────────────────
export const exitEventsApi = {
  list: (params?: { page?: number; limit?: number; assetId?: string }) =>
    api.get('/exit-events', { params }),
  get: (id: string) => api.get(`/exit-events/${id}`),
  create: (data: any) => api.post('/exit-events', data),
  update: (id: string, data: any) => api.put(`/exit-events/${id}`, data),
  delete: (id: string) => api.delete(`/exit-events/${id}`),
};

// ── Cash Flows API ────────────────────────────────────────
export const cashFlowsApi = {
  list: () => api.get('/cash-flows'),
  create: (data: any) => api.post('/cash-flows', data),
  update: (id: string, data: any) => api.put(`/cash-flows/${id}`, data),
  delete: (id: string) => api.delete(`/cash-flows/${id}`),
};

// ── Liquidity API ─────────────────────────────────────────
export const liquidityApi = {
  projection: (years = 6) => api.get(`/liquidity/projection?years=${years}`),
  stress: (years = 6) => api.get(`/liquidity/stress?years=${years}`),
  kpis: () => api.get('/liquidity/kpis'),
};
